import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useOpenPresignedFile } from "../../hooks/useOpenPresignedFile";
import { backendService } from "../../services/backend.service";

/**
 * The raw source file behind a sync decision, and who may have it.
 *
 * The file is NETWORK-wide (one Paycom export covers every facility), so downloading it is support-only
 * — mirroring the support-gated `/sync-lineage` endpoint that mints the presigned link. Everyone else
 * gets the key as copyable text, so no one can pull another facility's data from a dept-scoped page.
 *
 * Shared by the log's Source column and the single-shift focus panel so both grant exactly the same
 * access; a caller only decides how to render the affordance.
 */
export const useRawSyncFile = (): { isSupport: boolean; openRawFile: (syncRunId: string) => Promise<void>; copyFileKey: (fileS3Key: string) => void } => {
	const { t } = useTranslation();
	const { userInfo } = useAuth();
	const { copyToClipboard } = useCopyToClipboard();
	const { openPresignedFile } = useOpenPresignedFile();

	const openRawFile = (syncRunId: string): Promise<void> =>
		openPresignedFile({
			fetchUrl: () => backendService.getSyncRunFileUrl(syncRunId),
			errorMessage: t("employeeChangelog.shiftLineage.file.downloadError"),
			analyticsEvent: "nurse_shift_lineage_raw_file_open",
			analyticsProps: { sync_run_id: syncRunId },
			action: "open raw sync file",
		});

	return { isSupport: !!userInfo?.support, openRawFile, copyFileKey: (fileS3Key: string) => void copyToClipboard(fileS3Key) };
};
